import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';

import { handleError } from 'services/ErrorManagement';
import { getFile, postFile } from 'services/Http';

export const MultiSegmentUploadNotes = [
    {
        title: 'Payer Segment Tips',
        notes: ['The same file is applied to every selected payer segment.'],
    },
];

const PRIOR_AUTH_UPLOAD_TIP_TITLE = 'File Upload Tips - Prior Authorization Required?';

export const ProductPayerConfigUploadNotes = [
    {
        title: 'Generate File Tips',
        notes: [
            'This will generate a spreadsheet template with 4 columns: OID, Start Date, End Date, and Prior Authorization Required?.',
        ],
    },
    {
        title: 'File Completion Tips',
        notes: [
            'OID is a required field.',
            'Start Date is an optional field.',
            'End Date is an optional field.',
            'Prior Authorization Required? is a required field.',
            'The generated spreadsheet will automatically format Start Date and End Dates as: YYYY-MM-DD.',
        ],
    },
    {
        title: 'File Upload Tips - OID',
        notes: [
            'The OID is required and must match an existing product service OID.',
            'The upload will create new product payer configurations with default values.',
            'Existing product payer configurations will keep their billing type, sites of care, and quantity defaults.',
            'Existing product payer configurations will not be deleted.',
        ],
    },
    {
        title: 'File Upload Tips - Start Date',
        notes: [
            'If the Start Date is left blank for a new product, the system will default to the current date.',
            'If the Start Date is left blank for an existing product, the existing start date will be kept.',
            'If the Start Date is in the past or today, the product config will be set to active immediately upon upload.',
            'If the Start Date is in the future, the product config will be set to active on that date.',
        ],
    },
    {
        title: 'File Upload Tips - End Date',
        notes: [
            'If the End Date is left blank for a new product, the product config will be set to never expire.',
            'If the End Date is left blank for an existing product, the existing end date will be kept.',
            'The End Date cannot be before the Start Date.',
        ],
    },
    {
        title: 'File Upload Tips - Prior Authorization Required?',
        notes: [
            'If Yes is entered, prior authorization is required.',
            'If No is entered, prior authorization is not required.',
            'Prior Authorization Required? is required and must be Yes or No.',
        ],
    },
];

export const ProductPayerConfigUploadNotesWithoutPriorAuth = ProductPayerConfigUploadNotes.filter(
    (note) => note.title !== PRIOR_AUTH_UPLOAD_TIP_TITLE,
).map((note) => {
    if (note.title === 'Generate File Tips') {
        return {
            ...note,
            notes: [
                'This will generate a spreadsheet template with 3 columns: OID, Start Date, and End Date.',
            ],
        };
    }
    if (note.title === 'File Completion Tips') {
        return {
            ...note,
            notes: note.notes.filter(
                (bullet) => bullet !== 'Prior Authorization Required? is a required field.',
            ),
        };
    }
    return note;
});

export const generateProductPayerConfigTemplate = async () => {
    try {
        saveAs(
            await getFile('/reference/product_benefit_configs/bulk_upload/template'),
            'Product Segment Config Bulk Upload.xlsx',
        );
    } catch (err) {
        handleError('An error occurred generating file. Please try again.', err);
    }
};

const postProductPayerConfigUpload = async (url, file, query) => {
    const validationErrors = await postFile(url, file, query);
    return validationErrors?.length > 0 ? validationErrors : null;
};

export const uploadProductPayerConfigFile = async (segmentIds, file, queryParams) =>
    postProductPayerConfigUpload('/reference/product_benefit_configs/bulk_upload', file, {
        ...queryParams,
        segment_ids: segmentIds,
    });

export const ValidationErrorsPropType = PropTypes.arrayOf(
    PropTypes.shape({
        sheet_name: PropTypes.string.isRequired,
        column_name: PropTypes.string.isRequired,
        row_index: PropTypes.number.isRequired,
        msg: PropTypes.string.isRequired,
    }),
);
